/**
 * InspectionModal — extracted from CleaningInspection.js (Phase 2 refactor).
 *
 * The inspection workflow modal: per-category checklist, focus mode for
 * mobile + keyboard nav, photo capture, signatures, save / complete actions.
 * Pure UI; all state + handlers come in as props so the parent (the page)
 * retains ownership of the heavy lifting (API persistence, localStorage,
 * `selectedInspection`, etc).
 *
 * NOTE: The inlined JSX is a verbatim lift from the original — no behavior
 * changes, only relocation. If you want to tighten this up later, the
 * candidate splits are: ChecklistCategory, ChecklistItemCard, ModalHeader.
 */
import {
  ClipboardCheck, Camera, Check, X, AlertTriangle, MapPin,
  CheckCircle2, XCircle, Image, Trash2, Eye,
  PenTool, BarChart3, Navigation, Filter, ChevronDown, ChevronUp,
  ArrowLeftRight,
} from 'lucide-react';

export default function InspectionModal({
  showInspectionModal,
  selectedInspection,
  setSelectedInspection,
  setShowInspectionModal,
  focusedItemId,
  setFocusedItemId,
  showCriticalOnly,
  setShowCriticalOnly,
  expandedCategories,
  inspectionChecklist,
  inspections,
  setInspections,
  updateChecklistItem,
  calculateProgress,
  calculateScore,
  getFilteredItems,
  getSectionCounts,
  handlePhotoCapture,
  removePhoto,
  completeInspection,
  saveInspectionToAPI,
  toggleCategory,
  bulkUpdateSection,
  setShowSignatureModal,
  setSignatureType,
}) {
  if (!showInspectionModal || !selectedInspection) return null;
  return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '20px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <div style={{ 
              padding: '20px', 
              borderBottom: '1px solid #e5e7eb',
              background: 'linear-gradient(135deg, #0073ea 0%, #0288d1 100%)',
              color: 'white'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>
                    Cleaning Inspection
                  </h2>
                  <p style={{ opacity: 0.9, fontSize: '14px' }}>
                    {selectedInspection.customerName} • {selectedInspection.address}
                  </p>
                </div>
                <button
                  onClick={() => setShowInspectionModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              
              {/* Progress Bar */}
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                  <span>Progress</span>
                  <span>{calculateProgress(selectedInspection)}%</span>
                </div>
                <div style={{ height: '8px', background: 'rgba(255,255,255,0.3)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ 
                    height: '100%', 
                    width: `${calculateProgress(selectedInspection)}%`,
                    background: 'white',
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>
            </div>

            {/* Quick Win: Critical Items Filter Toggle */}
            <div style={{ 
              padding: '12px 20px', 
              borderBottom: '1px solid #e5e7eb',
              background: '#fafafa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  <input
                    type="checkbox"
                    checked={showCriticalOnly}
                    onChange={(e) => setShowCriticalOnly(e.target.checked)}
                    style={{ accentColor: '#ef4444' }}
                  />
                  <AlertTriangle size={14} color="#ef4444" />
                  Show Critical Items Only
                </label>
              </div>
            </div>

            {/* Checklist Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
              {Object.entries(inspectionChecklist).map(([categoryKey, category]) => {
                const counts = getSectionCounts(categoryKey);
                const filteredItems = getFilteredItems(categoryKey);
                
                // Skip category if filtering and no critical items
                if (showCriticalOnly && filteredItems.length === 0) return null;
                
                return (
                <div key={categoryKey} style={{ marginBottom: '16px', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                  {/* Category Header with Quick Win Features */}
                  <div style={{ background: '#f9fafb', borderBottom: expandedCategories[categoryKey] ? '1px solid #e5e7eb' : 'none' }}>
                    <button
                      onClick={() => toggleCategory(categoryKey)}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '600', fontSize: '16px' }}>
                        <span style={{ fontSize: '20px' }}>{category.icon}</span>
                        {category.label}
                        {/* Quick Win: Section Status Counts */}
                        <div style={{ display: 'flex', gap: '4px', marginLeft: '8px' }}>
                          {counts.pass > 0 && (
                            <span style={{ fontSize: '11px', background: '#dcfce7', color: '#166534', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                              ✓ {counts.pass}
                            </span>
                          )}
                          {counts.fail > 0 && (
                            <span style={{ fontSize: '11px', background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                              ✗ {counts.fail}
                            </span>
                          )}
                          {counts.na > 0 && (
                            <span style={{ fontSize: '11px', background: '#f3f4f6', color: '#6b7280', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                              — {counts.na}
                            </span>
                          )}
                          {counts.unchecked > 0 && (
                            <span style={{ fontSize: '11px', background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px', fontWeight: '600' }}>
                              ? {counts.unchecked}
                            </span>
                          )}
                        </div>
                      </span>
                      {expandedCategories[categoryKey] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    
                    {/* Quick Win: Pass All / Fail All Buttons */}
                    {expandedCategories[categoryKey] && (
                      <div style={{ 
                        padding: '0 16px 12px', 
                        display: 'flex', 
                        gap: '8px',
                        borderTop: '1px dashed #e5e7eb',
                        paddingTop: '12px',
                        marginTop: '-4px'
                      }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); bulkUpdateSection(categoryKey, 'pass'); }}
                          style={{
                            padding: '6px 12px',
                            background: '#dcfce7',
                            color: '#166534',
                            border: '1px solid #bbf7d0',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <Check size={14} /> Pass All
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); bulkUpdateSection(categoryKey, 'fail'); }}
                          style={{
                            padding: '6px 12px',
                            background: '#fee2e2',
                            color: '#991b1b',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <X size={14} /> Fail All
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); bulkUpdateSection(categoryKey, 'na'); }}
                          style={{
                            padding: '6px 12px',
                            background: '#f3f4f6',
                            color: '#6b7280',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          N/A All
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Category Items */}
                  {expandedCategories[categoryKey] && (
                    <div style={{ padding: '8px 16px 16px' }}>
                      {filteredItems.map(item => {
                        const result = selectedInspection.checklist[categoryKey]?.[item.id] || {};
                        const itemKey = `${categoryKey}|${item.id}`;
                        const isFocused = focusedItemId === itemKey;
                        
                        return (
                          <div 
                            key={item.id}
                            onClick={() => setFocusedItemId(itemKey)}
                            style={{ 
                              padding: '12px',
                              borderBottom: '1px solid #f3f4f6',
                              background: result.status === 'pass' ? '#f0fdf4' : result.status === 'fail' ? '#fef2f2' : 'white',
                              cursor: 'pointer',
                              outline: isFocused ? '2px solid #0073ea' : 'none',
                              outlineOffset: '-2px',
                              borderRadius: isFocused ? '8px' : '0'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                              <div style={{ flex: 1 }}>
                                <span style={{ 
                                  fontWeight: '500',
                                  color: result.status === 'pass' ? '#166534' : result.status === 'fail' ? '#991b1b' : '#374151'
                                }}>
                                  {item.name}
                                  {item.critical && (
                                    <span style={{ 
                                      marginLeft: '8px', 
                                      fontSize: '10px', 
                                      background: '#fef3c7', 
                                      color: '#92400e',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}>
                                      CRITICAL
                                    </span>
                                  )}
                                </span>
                              </div>
                              
                              {/* Status Buttons — soft-tile pattern matching
                                  the Inspection List actions. Tinted when
                                  active, subtle gray outline when idle. */}
                              <div style={{ display: 'flex', gap: 6 }}>
                                {[
                                  { value: 'pass', label: 'Pass', tint: '#22c55e', icon: CheckCircle2 },
                                  { value: 'fail', label: 'Fail', tint: '#ef4444', icon: XCircle },
                                  { value: 'na', label: 'N/A', tint: '#64748b', icon: null },
                                ].map(({ value, label, tint, icon: Icon }) => {
                                  const active = result.status === value;
                                  return (
                                    <button
                                      key={value}
                                      onClick={() => updateChecklistItem(categoryKey, item.id, 'status', value)}
                                      data-testid={`item-${value}-${item.id}`}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        padding: '6px 12px',
                                        borderRadius: 9,
                                        border: active ? `1px solid ${tint}` : '1px solid #e2e8f0',
                                        background: active ? `${tint}1F` : '#fff',
                                        color: active ? tint : '#64748b',
                                        fontSize: 13, fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'background 0.15s ease, transform 0.15s ease, border-color 0.15s ease',
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!active) {
                                          e.currentTarget.style.background = `${tint}0F`;
                                          e.currentTarget.style.borderColor = `${tint}66`;
                                          e.currentTarget.style.color = tint;
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!active) {
                                          e.currentTarget.style.background = '#fff';
                                          e.currentTarget.style.borderColor = '#e2e8f0';
                                          e.currentTarget.style.color = '#64748b';
                                        }
                                      }}
                                    >
                                      {Icon && <Icon size={14} strokeWidth={2.25} />}
                                      {label}
                                    </button>
                                  );
                                })}
                                {/* Photo buttons — Before / Standard / After.
                                    Same soft-tile pattern as Pass/Fail/N/A. */}
                                <div style={{ display: 'flex', gap: 6 }}>
                                  {[
                                    { mode: 'before', label: 'Before', tint: '#f59e0b' },
                                    { mode: 'standard', label: 'Photo', tint: '#0073ea' },
                                    { mode: 'after', label: 'After', tint: '#22c55e' },
                                  ].map(({ mode, label, tint }) => {
                                    const hasPhoto = result.photos?.some(p => (
                                      mode === 'standard' ? (!p.mode || p.mode === 'standard') : p.mode === mode
                                    ));
                                    return (
                                      <button
                                        key={mode}
                                        onClick={() => handlePhotoCapture(categoryKey, item.id, mode)}
                                        title={`Attach a '${label}' photo`}
                                        data-testid={`photo-${mode}-${item.id}`}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', gap: 4,
                                          padding: '6px 12px',
                                          borderRadius: 9,
                                          border: hasPhoto ? `1px solid ${tint}` : '1px solid #e2e8f0',
                                          background: hasPhoto ? `${tint}1F` : '#fff',
                                          color: hasPhoto ? tint : '#64748b',
                                          fontSize: 13, fontWeight: 600,
                                          cursor: 'pointer',
                                          transition: 'background 0.15s ease, transform 0.15s ease, border-color 0.15s ease',
                                        }}
                                        onMouseEnter={(e) => {
                                          if (!hasPhoto) {
                                            e.currentTarget.style.background = `${tint}0F`;
                                            e.currentTarget.style.borderColor = `${tint}66`;
                                            e.currentTarget.style.color = tint;
                                          }
                                        }}
                                        onMouseLeave={(e) => {
                                          if (!hasPhoto) {
                                            e.currentTarget.style.background = '#fff';
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.color = '#64748b';
                                          }
                                        }}
                                      >
                                        <Camera size={14} strokeWidth={2.25} />
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Quick Win: Notes Input for ALL items (not just failed) */}
                            <div style={{ marginTop: '8px' }}>
                              <input
                                type="text"
                                placeholder={result.status === 'fail' ? "Add notes about the issue..." : "Add optional notes..."}
                                value={result.notes || ''}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => updateChecklistItem(categoryKey, item.id, 'notes', e.target.value)}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  border: result.status === 'fail' ? '1px solid #fca5a5' : '1px solid #e5e7eb',
                                  borderRadius: '6px',
                                  fontSize: '13px',
                                  background: result.status === 'fail' ? '#fef2f2' : '#fafafa'
                                }}
                              />
                            </div>

                            {/* Photos with Before/After Labels */}
                            {result.photos?.length > 0 && (
                              <div style={{ marginTop: '8px' }}>
                                {/* Before/After Comparison View */}
                                {(result.photos.some(p => p.mode === 'before') && result.photos.some(p => p.mode === 'after')) && (
                                  <div style={{ 
                                    display: 'flex', 
                                    gap: '8px', 
                                    marginBottom: '8px',
                                    padding: '8px',
                                    background: '#f0fdf4',
                                    borderRadius: '8px',
                                    alignItems: 'center'
                                  }}>
                                    <ArrowLeftRight size={16} color="#22c55e" />
                                    <span style={{ fontSize: '11px', fontWeight: '600', color: '#16a34a' }}>Before/After Comparison Available</span>
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {result.photos.map(photo => (
                                  <div key={photo.id} style={{ position: 'relative' }}>
                                    <img 
                                      src={photo.data} 
                                      alt="Inspection" 
                                      style={{ 
                                        width: '60px', 
                                        height: '60px', 
                                        objectFit: 'cover', 
                                        borderRadius: '8px',
                                        border: photo.mode === 'before' 
                                          ? '2px solid #f59e0b' 
                                          : photo.mode === 'after' 
                                            ? '2px solid #22c55e' 
                                            : '2px solid #e5e7eb'
                                      }} 
                                    />
                                    {/* Photo Mode Label */}
                                    {photo.mode && photo.mode !== 'standard' && (
                                      <div style={{
                                        position: 'absolute',
                                        top: '-6px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        background: photo.mode === 'before' ? '#f59e0b' : '#22c55e',
                                        color: 'white',
                                        fontSize: '8px',
                                        fontWeight: '700',
                                        padding: '1px 6px',
                                        borderRadius: '4px',
                                        textTransform: 'uppercase'
                                      }}>
                                        {photo.mode}
                                      </div>
                                    )}
                                    {/* GPS Indicator */}
                                    {photo.location && (
                                      <div style={{
                                        position: 'absolute',
                                        bottom: '2px',
                                        left: '2px',
                                        background: 'rgba(0,0,0,0.6)',
                                        borderRadius: '4px',
                                        padding: '1px 3px'
                                      }}>
                                        <Navigation size={8} color="white" />
                                      </div>
                                    )}
                                    <button
                                      onClick={() => removePhoto(categoryKey, item.id, photo.id)}
                                      style={{
                                        position: 'absolute',
                                        top: '-6px',
                                        right: '-6px',
                                        background: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <X size={12} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
              })}

              {/* Overall Notes */}
              <div style={{ marginTop: '20px' }}>
                <label style={{ fontWeight: '600', display: 'block', marginBottom: '8px' }}>
                  Overall Notes
                </label>
                <textarea
                  value={selectedInspection.overallNotes || ''}
                  onChange={(e) => {
                    const updated = { ...selectedInspection, overallNotes: e.target.value };
                    setSelectedInspection(updated);
                    const all = inspections.map(i => i.id === updated.id ? updated : i);
                    setInspections(all);
                    // Debounce API save for text input
                    saveInspectionToAPI(updated);
                  }}
                  placeholder="Add any overall observations or comments..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    minHeight: '100px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            {/* Signature Section */}
            <div style={{ 
              padding: '20px', 
              borderTop: '1px solid #e5e7eb',
              background: '#fafafa'
            }}>
              <h3 style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#374151', 
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <PenTool size={18} color="#0073ea" />
                Signatures
              </h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                {/* Inspector Signature */}
                <div style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '12px', 
                  padding: '16px',
                  background: 'white'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>
                    Inspector Signature
                  </div>
                  {selectedInspection.inspectorSignature ? (
                    <div>
                      <img 
                        src={selectedInspection.inspectorSignature} 
                        alt="Inspector signature"
                        style={{ maxWidth: '100%', height: '60px', objectFit: 'contain' }}
                      />
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                        Signed: {new Date(selectedInspection.inspectorSignedAt).toLocaleString()}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setSignatureType('inspector'); setShowSignatureModal(true); }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px dashed #d1d5db',
                        borderRadius: '8px',
                        background: 'transparent',
                        color: '#6b7280',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '13px'
                      }}
                    >
                      <PenTool size={16} /> Add Signature
                    </button>
                  )}
                </div>
                
                {/* Customer Signature */}
                <div style={{ 
                  border: '1px solid #e5e7eb', 
                  borderRadius: '12px', 
                  padding: '16px',
                  background: 'white'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#6b7280', marginBottom: '8px' }}>
                    Customer Signature
                  </div>
                  {selectedInspection.customerSignature ? (
                    <div>
                      <img 
                        src={selectedInspection.customerSignature} 
                        alt="Customer signature"
                        style={{ maxWidth: '100%', height: '60px', objectFit: 'contain' }}
                      />
                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                        Signed: {new Date(selectedInspection.customerSignedAt).toLocaleString()}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setSignatureType('customer'); setShowSignatureModal(true); }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px dashed #d1d5db',
                        borderRadius: '8px',
                        background: 'transparent',
                        color: '#6b7280',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '13px'
                      }}
                    >
                      <PenTool size={16} /> Add Signature
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div style={{ 
              padding: '16px 20px', 
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#f9fafb'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Score: <strong style={{ color: calculateScore(selectedInspection) >= 80 ? '#22c55e' : '#ef4444' }}>
                  {calculateScore(selectedInspection) || 0}%
                </strong>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={() => setShowInspectionModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Save & Close
                </button>
                <button
                  onClick={completeInspection}
                  disabled={calculateProgress(selectedInspection) < 100}
                  style={{
                    padding: '10px 20px',
                    background: calculateProgress(selectedInspection) === 100 ? '#22c55e' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: calculateProgress(selectedInspection) === 100 ? 'pointer' : 'not-allowed',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <CheckCircle2 size={18} /> Complete Inspection
                </button>
              </div>
            </div>
          </div>
        </div>
      );
}
